/**
 * Kleurhulp voor de huisstijl.
 *
 * Een organisatie geeft één kleur op. De tweede kleur — voor voortgangsbalken,
 * accenten en het merkteken — leiden we daaruit af, zodat niemand twee kleuren
 * hoeft te kiezen die bij elkaar passen. Wie het toch zelf wil bepalen, vult in
 * het huisstijlscherm een eigen tweede kleur in; die wint altijd.
 */

/** '#ab3' en '#aabb33' worden allebei {r,g,b}; onbruikbare invoer geeft null. */
export function hexNaarRgb(hex) {
    if (typeof hex !== 'string') return null;

    let waarde = hex.trim().replace('#', '');

    if (waarde.length === 3) {
        waarde = waarde.split('').map(teken => teken + teken).join('');
    }

    if (!/^[0-9a-fA-F]{6}$/.test(waarde)) return null;

    return {
        r: parseInt(waarde.slice(0, 2), 16),
        g: parseInt(waarde.slice(2, 4), 16),
        b: parseInt(waarde.slice(4, 6), 16),
    };
}

export function rgbNaarHsl({ r, g, b }) {
    const rood = r / 255, groen = g / 255, blauw = b / 255;
    const max = Math.max(rood, groen, blauw);
    const min = Math.min(rood, groen, blauw);
    const verschil = max - min;

    let tint = 0;
    if (verschil !== 0) {
        if (max === rood) tint = ((groen - blauw) / verschil) % 6;
        else if (max === groen) tint = (blauw - rood) / verschil + 2;
        else tint = (rood - groen) / verschil + 4;
    }
    tint = Math.round(tint * 60);
    if (tint < 0) tint += 360;

    const licht = (max + min) / 2;
    const verzadiging = verschil === 0 ? 0 : verschil / (1 - Math.abs(2 * licht - 1));

    return { h: tint, s: verzadiging, l: licht };
}

export function hslNaarHex({ h, s, l }) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let [r, g, b] = [0, 0, 0];
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    const naarHex = getal => Math.round((getal + m) * 255).toString(16).padStart(2, '0');
    return `#${naarHex(r)}${naarHex(g)}${naarHex(b)}`;
}

const klem = (waarde, laag, hoog) => Math.min(hoog, Math.max(laag, waarde));

/**
 * De tweede kleur: een buur van de accentkleur (34° verder in de kleurcirkel),
 * met een vaste helderheid. Die vaste helderheid is het punt — zo blijft de
 * kleur leesbaar op zowel het donkere laken als de lichte vlakken, ook als de
 * organisatie een bijna-zwarte of juist felle kleur kiest.
 */
export function leidTweedeKleurAf(accentHex) {
    const rgb = hexNaarRgb(accentHex);
    if (!rgb) return null;

    const { h, s } = rgbNaarHsl(rgb);

    return hslNaarHex({
        h: (h + 34) % 360,
        // Verzadiging bewust aan banden: een felle merkkleur levert anders een
        // neonkleur op die het scherm overneemt in plaats van hem te steunen.
        s: klem(s === 0 ? 0.42 : s * 0.8, 0.32, 0.55),
        l: 0.55,
    });
}

/** Zwarte of witte tekst op deze kleur — welke van de twee het beste leest. */
export function leesbareTekstkleur(hex) {
    const rgb = hexNaarRgb(hex);
    if (!rgb) return '#ffffff';

    // Relatieve helderheid volgens WCAG.
    const kanaal = waarde => {
        const deel = waarde / 255;
        return deel <= 0.03928 ? deel / 12.92 : Math.pow((deel + 0.055) / 1.055, 2.4);
    };
    const helderheid = 0.2126 * kanaal(rgb.r) + 0.7152 * kanaal(rgb.g) + 0.0722 * kanaal(rgb.b);

    return helderheid > 0.42 ? '#23201a' : '#fff8ef';
}
